// src/components/atoms/FormInputGroup.js

import React from "react";
import { Box, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { useTheme } from "styled-components"; // Assuming you're using styled-components for theming

const FormInputGroup = ({
  label,
  type = "text",
  value,
  onChange,
  placeholder,
  id,
  error = false,
  helperText = "",
  multiline = false, // Default to false
  rows = 4, // Default number of rows for textarea
  ...props
}) => {
  const theme = useTheme();

  return (
    <Box mb={2}>
      {/* Label for Accessibility */}
      <label htmlFor={id} style={{ display: "block", marginBottom: "8px" }}>
        <Typography variant="subtitle1" gutterBottom>
          {label}
        </Typography>
      </label>

      {/* Conditionally Render Input or Textarea */}
      {multiline ? (
        <textarea
          id={id}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          rows={rows}
          required={props.required}
          style={{
            width: "100%",
            padding: "10px",
            borderRadius: "4px",
            border: `1px solid ${
              error
                ? theme.colors.error || "#f44336"
                : theme.colors.gray || "#ccc"
            }`,
            backgroundColor: theme.colors.white || "#ffffff",
            fontFamily: "basic-sans, sans-serif",
            fontSize: "16px",
            color: "#000",
            outline: "none",
            boxSizing: "border-box",
            resize: "vertical", // Allow vertical resizing
          }}
          {...props}
        />
      ) : (
        <input
          id={id}
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={props.required}
          style={{
            width: "100%",
            padding: "10px",
            borderRadius: "4px",
            border: `1px solid ${
              error
                ? theme.colors.error || "#f44336"
                : theme.colors.gray || "#ccc"
            }`,
            backgroundColor: theme.colors.white || "#ffffff",
            fontFamily: "basic-sans, sans-serif",
            fontSize: "16px",
            color: "#000",
            outline: "none",
            boxSizing: "border-box",
          }}
          {...props}
        />
      )}

      {/* Helper Text for Errors */}
      {error && (
        <small
          style={{
            display: "block",
            marginTop: "4px",
            fontSize: "12px",
            color: theme.colors.error || "#f44336",
            fontFamily: "basic-sans, sans-serif",
          }}
        >
          {helperText}
        </small>
      )}
    </Box>
  );
};

FormInputGroup.propTypes = {
  label: PropTypes.string.isRequired, // Label for the input
  type: PropTypes.string, // Type of the input (e.g., text, password, email)
  value: PropTypes.string.isRequired, // Current value of the input
  onChange: PropTypes.func.isRequired, // Function to handle input changes
  placeholder: PropTypes.string, // Placeholder text inside the input
  id: PropTypes.string.isRequired, // Unique identifier for accessibility
  error: PropTypes.bool, // Indicates if there's an error
  helperText: PropTypes.string, // Error message or helper text
  multiline: PropTypes.bool, // Determines if the input is multiline
  rows: PropTypes.number, // Number of rows for textarea
};

export default FormInputGroup;
